<script>
import Layout from '@layouts/loginLayout.vue'
import { loadResetAccountAccessForm, submitResetAccountAccessForm } from '@src/api/auth'
import _ from 'lodash'

export default {
  page: {
    title: 'Reset Account Access',
    meta: [
      {
        name: 'description',
        content: 'The Reset Account Access page.',
      },
    ],
  },
  components: { Layout },
  data() {
    return {
      recoveryQuestions: '',
      userName: 'User',
      userEmail: '',
      password: '',
      confirmPassword: '',
      recoveryQuestion: '',
      recoveryAnswer: '',
      initialLoading: true,
      loading: false,
      successfully: false,
    }
  },
  computed: {
    placeholders() {
      return {
        password: 'New Password',
        confirmPassword: 'Confirm New Password',
        recoveryQuestion: 'Security Question',
        recoveryAnswer: 'Security Answer',
      }
    },

    canPressButton() {
      if (!this.password || !this.confirmPassword || !this.recoveryQuestion || !this.recoveryAnswer) {
        return false
      }

      return !this.loading
    },

    confirmPasswordInvalid() {
      return this.password !== this.confirmPassword
    },
  },
  mounted() {
    this.loadResetAccountAccessForm()
  },
  methods: {
    async loadResetAccountAccessForm() {
      this.authError = null
      try {
        await loadResetAccountAccessForm({
          oktaId: this.$route.params.oktaId,
        }).then((result) => {
          this.recoveryQuestions = result.data.data
          this.userName = result.data.userName
          this.userEmail = result.data.userEmail
          this.initialLoading = false
        })
      } catch (err) {
        this.initialLoading = false
        this.toastLinkError()
      }
    },

    async submitResetAccountAccessForm() {
      this.authError = null
      if (this.confirmPasswordInvalid) {
        this.authError = 'The password and confirm password fields do not match.'
        this.toastError()
        return
      }
      this.loading = true
      try {
        await submitResetAccountAccessForm({
          oktaId: this.$route.params.oktaId,
          password: this.password,
          recoveryQuestion: this.recoveryQuestion,
          recoveryAnswer: this.recoveryAnswer,
        }).then(() => {
          this.toastSuccess()
        })
      } catch (err) {
        this.authError = 'Server Error. Please try again in a few moments.'
        const response = _.get(err, 'response.data.data', 'Server Error. Please try again in a few moments.')
        if (typeof response === 'string') {
          this.authError = response
        }
        if (typeof response === 'object') {
          if (typeof response.password === 'object') {
            this.authError = response.password.join(' ')
          }
          if (typeof response.newPassword === 'object') {
            this.authError = response.newPassword.join(' ')
          }
        }
        if (this.authError.indexOf('recoveryQuestion.answer:') > 0) {
          this.authError = 'The answer to the recovery question is incorrect. Please try again.'
        }
        this.authError = this.authError.replace('Okta Error: password.value: ', '')
        this.authError = this.authError.replace('Okta Error: password: ', '')
        this.authError = this.authError.replace('Okta Error: newPassword.value: ', '')
        this.authError = this.authError.replace('Okta Error: securityAnswer: ', 'Invalid Security Answer. ')
        this.authError = this.authError.replace('Okta Error: ', '')
        this.toastError()
        this.loading = false
      }
    },

    toastError() {
      this.$bvToast.toast(this.authError, {
        toaster: 'b-toaster-bottom-center',
        toastClass: 'toast-error',
      })
    },

    toastLinkError() {
      const toastMessage = 'You have not been created in the system. Please contact support.'
      this.$bvToast.toast(toastMessage, {
        toaster: 'b-toaster-bottom-center',
        toastClass: 'toast-error',
        noAutoHide: true,
      })
      this.loading = false
      this.successfully = true
    },

    toastSuccess() {
      const toastMessage = 'Account Access Reset Complete! You may now log into Motto Center.'
      this.$bvToast.toast(toastMessage, {
        toaster: 'b-toaster-bottom-center',
        toastClass: 'toast-success',
        noAutoHide: true,
      })
      this.loading = false
      this.successfully = true
    },

    handleType(event) {
      const { srcElement, type } = event
      const { name, value } = srcElement

      if (type === 'blur' && !value) {
        this.fieldTypes[name] = 'text'
      } else {
        this.fieldTypes[name] = 'password'
      }
    },
  },
}
</script>

<template>
  <Layout>
    <form @submit.prevent="submitResetAccountAccessForm">
      <BaseIcon v-if="initialLoading" name="spinner" spin />

      <div v-else-if="!successfully">
        <h1 class="motto-blue-text mb-20">Account Access Reset for {{ userName }}</h1>

        <h3 class="motto-blue-text mb-45">{{ userEmail }}</h3>

        <BaseFormGroup :label="placeholders.password" class="mb-40">
          <BaseInputPassword v-model="password" name="password" type="text" :placeholder="placeholders.password" />
        </BaseFormGroup>

        <BaseFormGroup :label="placeholders.confirmPassword" class="mb-40">
          <BaseInputPassword
            v-model="confirmPassword"
            name="confirmPassword"
            type="text"
            :placeholder="placeholders.confirmPassword" />
        </BaseFormGroup>

        <BaseFormGroup :label="placeholders.recoveryQuestion" class="mb-40">
          <BaseSelect
            v-model="recoveryQuestion"
            name="recoveryQuestion"
            empty-text="-- Select a Security Question --"
            :options="recoveryQuestions"
            option-text="questionText"
            option-value="questionText" />
        </BaseFormGroup>

        <BaseFormGroup :label="placeholders.recoveryAnswer" class="mb-55">
          <BaseInputText
            v-model="recoveryAnswer"
            autocomplete="off"
            name="recoveryAnswer"
            :placeholder="placeholders.recoveryAnswer" />
        </BaseFormGroup>

        <BaseButton :disabled="!canPressButton" type="submit">
          <BaseIcon v-if="loading" name="spinner" spin />
          <span v-else>Save Account Information</span>
        </BaseButton>
      </div>

      <div v-else>
        <h1 class="motto-blue-text mb-55">Account Access Reset for {{ userName }}</h1>

        <BaseLink :to="{ name: 'login' }">
          <BaseButton type="button">
            <span>Go to Login Page</span>
          </BaseButton>
        </BaseLink>
      </div>
    </form>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';
</style>
